import { useEffect, useSyncExternalStore } from "react";
import Swal from "sweetalert2";
import api from "../../../utils/api";
import type { DealDemoPointer } from "./dealDemoService";

// =====================================================================// CRM Deals — backend-backed store.
//
// Powers two real, interactive surfaces under /crm/panel:
//   • Pipeline (Kanban)  — drag deals between stages, win/lose, CRUD
//   • Pronóstico (Forecast) — weighted revenue projection vs. team quota
//
// Both screens read the SAME store, so a deal moved on the board immediately
// re-weights the forecast. Deals live in the `crm_deals` Firestore collection
// (torre-de-control-api → crmDeals module). Every new lead automatically gets a
// card here via the onLeadCreated trigger, so the board mirrors the leads
// pipeline without manual data entry.
//
// Mutations are optimistic: the local store updates instantly for a snappy UI,
// then syncs to the API and reconciles (reloading on failure).
// =====================================================================
// --- Stages -----------------------------------------------------------------
// Each pipeline stage carries a default win probability and a forecast
// category, mirroring how HubSpot / GoHighLevel weight an opportunity.

export type DealStage =
  | "nuevo"
  | "contactado"
  | "agendado"
  | "calificado"
  | "propuesta"
  | "negociacion"
  | "ganado"
  | "terminado"
  | "congelado"
  | "perdido";

export type ForecastCategory = "pipeline" | "best_case" | "commit" | "won" | "frozen" | "lost";

export interface StageMeta {
  id: DealStage;
  label: string;
  /** Default win probability (0-100) when a deal doesn't override it. */
  probability: number;
  /** Forecast bucket this stage rolls up into. */
  category: ForecastCategory;
  /** Tailwind accent for the column header / dots. */
  accent: string;
  dot: string;
  /** Tailwind tone for the stage pill in lists (same hue as accent/dot). */
  pill: string;
  /** True for the two terminal stages (ganado / perdido). */
  closed?: boolean;
  won?: boolean;
}

export const STAGES: StageMeta[] = [
  { id: "nuevo", label: "Nuevo", probability: 10, category: "pipeline", accent: "text-sky-500", dot: "bg-sky-500", pill: "bg-sky-500/15 text-sky-600 dark:text-sky-400" },
  { id: "contactado", label: "Contactado", probability: 25, category: "pipeline", accent: "text-indigo-500", dot: "bg-indigo-500", pill: "bg-indigo-500/15 text-indigo-600 dark:text-indigo-400" },
  { id: "agendado", label: "Agendado", probability: 40, category: "best_case", accent: "text-cyan-500", dot: "bg-cyan-500", pill: "bg-cyan-500/15 text-cyan-600 dark:text-cyan-400" },
  { id: "calificado", label: "Calificado", probability: 50, category: "best_case", accent: "text-violet-500", dot: "bg-violet-500", pill: "bg-violet-500/15 text-violet-600 dark:text-violet-400" },
  { id: "propuesta", label: "Propuesta", probability: 70, category: "best_case", accent: "text-amber-500", dot: "bg-amber-500", pill: "bg-amber-500/15 text-amber-600 dark:text-amber-400" },
  { id: "negociacion", label: "Negociación", probability: 90, category: "commit", accent: "text-orange-500", dot: "bg-orange-500", pill: "bg-orange-500/15 text-orange-600 dark:text-orange-400" },
  { id: "ganado", label: "Ganado", probability: 100, category: "won", accent: "text-emerald-500", dot: "bg-emerald-500", pill: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400", closed: true, won: true },
  // Proyecto entregado y cerrado (#1043): cuenta como ganado en el pronóstico.
  { id: "terminado", label: "Terminado", probability: 100, category: "won", accent: "text-teal-500", dot: "bg-teal-500", pill: "bg-teal-500/15 text-teal-600 dark:text-teal-400", closed: true, won: true },
  // La compañía de plataforma dejó de cobrar por un tiempo (Baja temporal,
  // Suspendido, No facturable) (#1069): no suma al pronóstico ni es perdido.
  { id: "congelado", label: "Congelado", probability: 0, category: "frozen", accent: "text-slate-400", dot: "bg-slate-400", pill: "bg-slate-400/15 text-slate-500", closed: true, won: false },
  { id: "perdido", label: "Perdido", probability: 0, category: "lost", accent: "text-red-500", dot: "bg-red-500", pill: "bg-red-500/15 text-red-500", closed: true, won: false },
];

/** Stages shown as draggable Kanban columns (terminal stages handled apart). */
export const OPEN_STAGES = STAGES.filter((s) => !s.closed);

export const STAGE_BY_ID: Record<DealStage, StageMeta> = STAGES.reduce(
  (acc, s) => ((acc[s.id] = s), acc),
  {} as Record<DealStage, StageMeta>,
);

export const FORECAST_CATEGORY_META: Record<
  ForecastCategory,
  { label: string; tone: string; bar: string }
> = {
  commit: { label: "Comprometido", tone: "text-emerald-600 dark:text-emerald-400", bar: "bg-emerald-500" },
  best_case: { label: "Mejor caso", tone: "text-amber-600 dark:text-amber-400", bar: "bg-amber-500" },
  pipeline: { label: "Pipeline", tone: "text-sky-600 dark:text-sky-400", bar: "bg-sky-500" },
  won: { label: "Ganado", tone: "text-emerald-600 dark:text-emerald-400", bar: "bg-emerald-600" },
  frozen: { label: "Congelado", tone: "text-slate-500", bar: "bg-slate-400" },
  lost: { label: "Perdido", tone: "text-red-500", bar: "bg-red-500" },
};

// --- Owners (sales reps) ----------------------------------------------------
// Owners are still a fixed roster (no backend owners collection yet). A deal's
// `ownerId` references one of these; auto-created cards start unassigned.

export interface Owner {
  id: string;
  name: string;
  initials: string;
  color: string; // avatar bg
  /** Monthly revenue quota (MXN). */
  quota: number;
}

export const OWNERS: Owner[] = [
  { id: "u-ana", name: "Ana Torres", initials: "AT", color: "bg-rose-500", quota: 450000 },
  { id: "u-daniel", name: "Daniel Ruiz", initials: "DR", color: "bg-sky-500", quota: 400000 },
  { id: "u-sofia", name: "Sofía Mena", initials: "SM", color: "bg-violet-500", quota: 380000 },
  { id: "u-carlos", name: "Carlos Lara", initials: "CL", color: "bg-amber-500", quota: 320000 },
];

export const OWNER_BY_ID: Record<string, Owner> = OWNERS.reduce(
  (acc, o) => ((acc[o.id] = o), acc),
  {} as Record<string, Owner>,
);

export type DealPriority = "low" | "medium" | "high";

export const PRIORITY_META: Record<DealPriority, { label: string; tone: string }> = {
  high: { label: "Alta", tone: "bg-red-500/15 text-red-500" },
  medium: { label: "Media", tone: "bg-amber-500/15 text-amber-600 dark:text-amber-400" },
  low: { label: "Baja", tone: "bg-gray-400/15 text-gray-500" },
};

// --- Deal -------------------------------------------------------------------

export type BillingFrequency = "none" | "monthly" | "biweekly";

/**
 * Cobro recurrente del negocio: en cada día de corte se genera la factura del
 * periodo y se manda a `emails` (ver Negocios → Facturas).
 */
export interface DealBilling {
  frequency: BillingFrequency;
  /** Día(s) de corte del mes: uno si es mensual, dos si es quincenal. */
  days: number[];
  /** Monto por periodo. null = sin definir (no se factura solo). */
  amount: number | null;
  /** Cuánto del monto va a cada cuenta de depósito; si existe, el monto es su suma. */
  breakdown?: { label: string; amount: number }[];
  /** Correos del cliente que reciben la factura y la confirmación de pago. */
  emails: string[];
  /** Días para pagar a partir del corte. */
  dueDays: number | null;
  /** Desde cuándo se factura (lo fija el backend al activar el cobro). */
  since?: string | null;
}

// --- Cierre del negocio (#1524) --------------------------------------------
// Lo que pasa después de la demo: cotización aceptada, pago/licencia,
// onboarding y, si se pierde, el motivo y cuándo volver a intentarlo. Lo
// escribe sobre todo el backend (cotización aceptada, activación de licencia);
// el motivo de pérdida lo captura ventas al mover el negocio a «Perdido».

export type PaymentStatus = "pendiente" | "pagado" | "exento";
export type OnboardingStatus = "pendiente" | "activo";
export type LostReason = "precio" | "sin_respuesta" | "competencia" | "sin_necesidad" | "otro";

export const LOST_REASONS: { id: LostReason; label: string }[] = [
  { id: "precio", label: "Precio" },
  { id: "sin_respuesta", label: "Sin respuesta" },
  { id: "competencia", label: "Se fue con la competencia" },
  { id: "sin_necesidad", label: "Sin necesidad" },
  { id: "otro", label: "Otro" },
];
export const LOST_REASON_LABEL: Record<LostReason, string> = LOST_REASONS.reduce(
  (acc, r) => ((acc[r.id] = r.label), acc),
  {} as Record<LostReason, string>,
);
export const PAYMENT_STATUS_LABEL: Record<PaymentStatus, string> = { pendiente: "Pendiente", pagado: "Pagado", exento: "Exento (licencia gratis)" };
export const ONBOARDING_STATUS_LABEL: Record<OnboardingStatus, string> = { pendiente: "Pendiente", activo: "Activo" };

export interface DealClosing {
  /** Cotización (crm_quotes) aceptada y cuándo. */
  quoteId?: string | null;
  quoteAcceptedAt?: string | null;
  paymentStatus?: PaymentStatus | null;
  paidAt?: string | null;
  /** Licencia activada en la plataforma del producto. */
  licenseSlug?: string | null;
  onboardingStatus?: OnboardingStatus | null;
  activatedAt?: string | null;
  /** Motivo (catálogo) y nota al marcar «Perdido». */
  lostReason?: LostReason | null;
  lostReasonNote?: string | null;
  /** Cuándo volver a contactar un negocio perdido (lo fija el backend a 30 días). */
  followUpAt?: string | null;
  /** Nota del sistema (p. ej. «Activó licencia gratis el 2026-10-17» en un perdido). */
  note?: string | null;
}

const isLostReason = (v: unknown): v is LostReason => typeof v === "string" && v in LOST_REASON_LABEL;

function closingFromApi(raw: unknown): DealClosing | null {
  if (!raw || typeof raw !== "object") return null;
  const c = raw as Record<string, unknown>;
  const str = (v: unknown) => (typeof v === "string" && v ? v : null);
  return {
    quoteId: str(c.quoteId),
    quoteAcceptedAt: str(c.quoteAcceptedAt),
    paymentStatus: c.paymentStatus === "pendiente" || c.paymentStatus === "pagado" || c.paymentStatus === "exento" ? c.paymentStatus : null,
    paidAt: str(c.paidAt),
    licenseSlug: str(c.licenseSlug),
    onboardingStatus: c.onboardingStatus === "pendiente" || c.onboardingStatus === "activo" ? c.onboardingStatus : null,
    activatedAt: str(c.activatedAt),
    lostReason: isLostReason(c.lostReason) ? c.lostReason : null,
    lostReasonNote: str(c.lostReasonNote),
    followUpAt: str(c.followUpAt),
    note: str(c.note),
  };
}

/** One stage a deal went through in HubSpot, with the dates HubSpot recorded. */
export interface DealStageStep {
  stageId: string;
  label: string;
  order?: number | null;
  pipelineId?: string | null;
  pipelineLabel?: string | null;
  kind?: "open" | "won" | "lost";
  enteredAt: string;
  exitedAt?: string | null;
}

export interface Deal {
  id: string;
  title: string;
  company: string;
  contact: string;
  amount: number; // MXN
  stage: DealStage;
  ownerId: string;
  closeDate: string; // YYYY-MM-DD (expected close)
  createdAt: string; // YYYY-MM-DD
  /** Optional manual probability override (0-100); falls back to stage default. */
  probability?: number | null;
  priority: DealPriority;
  source: string;
  notes?: string;
  lostReason?: string;
  /** Set on cards auto-created from a lead — links back to crm_leads. */
  leadId?: string | null;
  /** Lo que costó el negocio (MXN). null = no se conoce (no es 0). */
  cost?: number | null;
  /**
   * #1491: reembolsos de nómina cargados a este negocio (o a su lead), en la
   * moneda del negocio. Lo calcula el servidor; se suma al costo capturado.
   */
  reimbursedCost?: number;
  /** Razones sociales con las que el cliente paga (puede depositar con varias). */
  payers?: string[];
  /** Horas de desarrollo invertidas (base del costo). null = no se conoce. */
  hours?: number | null;
  /** Empresa del CRM (crm_companies) del negocio. */
  companyId?: string | null;
  /** Compañía de la plataforma a la que sigue (su estado mueve la etapa, #1069). */
  platformCompanyId?: string | null;
  /** Cobro recurrente: frecuencia, días de corte, monto y correos de cobranza. */
  billing?: DealBilling;
  /** Producto del negocio (Coding Cloud, Wayak, Nuvex…); filtra el tablero. */
  product?: string | null;
  /** Etapa del negocio en HubSpot tal cual (id), con su nombre y posición. */
  hubspotStage?: string | null;
  hubspotStageLabel?: string | null;
  hubspotStageOrder?: number | null;
  /** Pipeline de HubSpot al que pertenece (id y nombre). */
  hubspotPipeline?: string | null;
  hubspotPipelineLabel?: string | null;
  /** Recorrido del negocio por el pipeline de HubSpot, del más antiguo al actual. */
  stageHistory?: DealStageStep[];
  /** Cotización adjunta de la que salió el valor del negocio (un adjunto importado de HubSpot). */
  quoteAttachmentId?: string | null;
  quoteAttachmentName?: string | null;
  /** Lo que dice esa cotización: mensualidad, pagos únicos y cómo se calculó el valor. */
  quotedMonthly?: number | null;
  quotedOneTime?: number | null;
  amountKind?: "mensual" | "proyecto" | null;
  amountNote?: string | null;
  /**
   * Puntero a su cuenta de demostración en la plataforma del producto (#1318).
   * Sólo el puntero: el detalle de la cuenta se lee en vivo (dealDemoService).
   */
  demo?: DealDemoPointer | null;
  /** Cierre del negocio (#1524): cotización, pago, licencia, onboarding, motivo de pérdida. */
  closing?: DealClosing | null;
}

export type DealDraft = Omit<Deal, "id" | "createdAt"> & {
  id?: string;
  createdAt?: string;
};

/** Effective win probability for a deal (override or stage default). */
export function dealProbability(deal: Deal): number {
  if (typeof deal.probability === "number") return deal.probability;
  return STAGE_BY_ID[deal.stage]?.probability ?? 0;
}

/** Probability-weighted value used by the forecast. */
export function weightedValue(deal: Deal): number {
  return Math.round((deal.amount * dealProbability(deal)) / 100);
}

export function isOpen(deal: Deal): boolean {
  return !STAGE_BY_ID[deal.stage]?.closed;
}

// --- API mapping ------------------------------------------------------------
// The backend stores a slightly looser shape (nullable fields, Firestore
// timestamps). Normalize it to the strict `Deal` the UI expects.

interface FirestoreTimestamp {
  _seconds?: number;
  seconds?: number;
}

/** A Firestore timestamp / ISO string → "YYYY-MM-DD" (or "" when unknown). */
function toISODate(value: FirestoreTimestamp | string | null | undefined): string {
  if (!value) return "";
  if (typeof value === "string") {
    const d = new Date(value);
    return isNaN(d.getTime()) ? value.slice(0, 10) : d.toISOString().slice(0, 10);
  }
  const seconds = value._seconds ?? value.seconds;
  if (typeof seconds === "number") return new Date(seconds * 1000).toISOString().slice(0, 10);
  return "";
}

type RawDeal = Partial<Deal> & {
  id: string;
  createdAt?: FirestoreTimestamp | string | null;
  closeDate?: string | null;
};

function fromApi(raw: RawDeal): Deal {
  const stage = (STAGE_BY_ID[raw.stage as DealStage] ? raw.stage : "nuevo") as DealStage;
  return {
    id: raw.id,
    title: raw.title || "Sin título",
    company: raw.company || "",
    contact: raw.contact || "",
    amount: Number(raw.amount) || 0,
    stage,
    ownerId: raw.ownerId || "",
    closeDate: raw.closeDate || "",
    createdAt: toISODate(raw.createdAt) || "2026-01-01",
    probability: typeof raw.probability === "number" ? raw.probability : null,
    priority: (raw.priority as DealPriority) || "medium",
    source: raw.source || "—",
    notes: raw.notes || undefined,
    lostReason: raw.lostReason || undefined,
    leadId: raw.leadId ?? null,
    cost: typeof raw.cost === "number" ? raw.cost : null,
    reimbursedCost: typeof raw.reimbursedCost === "number" ? raw.reimbursedCost : 0,
    payers: Array.isArray(raw.payers) ? raw.payers.map(String) : [],
    hours: typeof raw.hours === "number" ? raw.hours : null,
    companyId: raw.companyId ?? null,
    platformCompanyId: raw.platformCompanyId ?? null,
    billing: raw.billing
      ? {
          frequency: raw.billing.frequency === "monthly" || raw.billing.frequency === "biweekly" ? raw.billing.frequency : "none",
          days: Array.isArray(raw.billing.days) ? raw.billing.days.map(Number) : [],
          amount: typeof raw.billing.amount === "number" ? raw.billing.amount : null,
          breakdown: Array.isArray(raw.billing.breakdown) ? raw.billing.breakdown.map((b) => ({ label: String(b.label), amount: Number(b.amount) || 0 })) : [],
          emails: Array.isArray(raw.billing.emails) ? raw.billing.emails.map(String) : [],
          dueDays: typeof raw.billing.dueDays === "number" ? raw.billing.dueDays : null,
          since: raw.billing.since ?? null,
        }
      : undefined,
    product: raw.product || null,
    hubspotStage: raw.hubspotStage || null,
    hubspotStageLabel: raw.hubspotStageLabel || null,
    hubspotStageOrder: typeof raw.hubspotStageOrder === "number" ? raw.hubspotStageOrder : null,
    hubspotPipeline: raw.hubspotPipeline || null,
    hubspotPipelineLabel: raw.hubspotPipelineLabel || null,
    stageHistory: Array.isArray(raw.stageHistory) ? raw.stageHistory.filter((s) => s && s.stageId && s.enteredAt) : [],
    quoteAttachmentId: raw.quoteAttachmentId || null,
    quoteAttachmentName: raw.quoteAttachmentName || null,
    quotedMonthly: typeof raw.quotedMonthly === "number" ? raw.quotedMonthly : null,
    quotedOneTime: typeof raw.quotedOneTime === "number" ? raw.quotedOneTime : null,
    amountKind: raw.amountKind === "mensual" || raw.amountKind === "proyecto" ? raw.amountKind : null,
    amountNote: raw.amountNote || null,
    demo: raw.demo && typeof raw.demo === "object" && raw.demo.state ? raw.demo : null,
    closing: closingFromApi(raw.closing),
  };
}

/** Fields the backend accepts on create/update. */
function toApi(deal: Partial<Deal>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const keys: (keyof Deal)[] = [
    "title", "company", "contact", "amount", "stage", "ownerId",
    "closeDate", "probability", "priority", "source", "notes", "lostReason",
    "cost", "payers", "hours", "billing", "closing",
  ];
  for (const k of keys) if (deal[k] !== undefined) out[k] = deal[k];
  return out;
}

// --- Store ------------------------------------------------------------------
// Minimal external store with a useSyncExternalStore subscription so Pipeline
// and Forecast share one reactive source of truth, hydrated from the API.

let deals: Deal[] = [];
const listeners = new Set<() => void>();

type LoadState = "idle" | "loading" | "loaded";
let loadState: LoadState = "idle";

function emit() {
  listeners.forEach((l) => l());
}

function setDeals(next: Deal[]) {
  deals = next;
  emit();
}

let idSeq = 0;
function tempId(): string {
  idSeq += 1;
  return `temp-${idSeq.toString(36)}`;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): Deal[] {
  return deals;
}

/** Fetch all deals from the API once (idempotent). */
async function loadDeals(force = false): Promise<void> {
  if (loadState === "loading") return;
  if (loadState === "loaded" && !force) return;
  loadState = "loading";
  try {
    const res = await api.get("/crm/dealsList", { params: { limit: 1000 } });
    const body = res.data ?? {};
    const list = Array.isArray(body.data) ? body.data : [];
    deals = list.map(fromApi);
    loadState = "loaded";
    emit();
  } catch (err) {
    loadState = "idle"; // allow a retry on next mount / refresh
    // eslint-disable-next-line no-console
    console.error("[dealsService] failed to load deals:", err);
  }
}

/** React hook: live list of deals shared across screens (hydrates on mount). */
export function useDeals(): Deal[] {
  const snap = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  useEffect(() => {
    void loadDeals();
  }, []);
  return snap;
}

/** True while the first load is in flight (for spinners). */
export function dealsLoading(): boolean {
  return loadState !== "loaded";
}

// --- Mutators (optimistic + API sync) ---------------------------------------

export function addDeal(draft: DealDraft): Deal {
  const optimistic: Deal = {
    ...draft,
    id: draft.id ?? tempId(),
    createdAt: draft.createdAt ?? mostRecentDate(),
  };
  setDeals([optimistic, ...deals]);

  api
    .post("/crm/deals", toApi(optimistic))
    .then((res) => {
      const created = res.data?.data ? fromApi(res.data.data) : null;
      if (created) {
        setDeals(deals.map((d) => (d.id === optimistic.id ? created : d)));
      }
    })
    .catch((err) => {
      // eslint-disable-next-line no-console
      console.error("[dealsService] addDeal failed:", err);
      void loadDeals(true);
    });

  return optimistic;
}

/** Mensaje de un error del API (`data.message` del envelope) o uno genérico. */
export function apiErrorMessage(err: unknown, fallback = "Inténtalo de nuevo."): string {
  const e = err as { response?: { data?: { data?: { message?: string }; message?: string } }; message?: string } | null;
  return e?.response?.data?.data?.message || e?.response?.data?.message || e?.message || fallback;
}

export function updateDeal(id: string, patch: Partial<Deal>): Promise<void> {
  const prev = deals;
  setDeals(deals.map((d) => (d.id === id ? { ...d, ...patch } : d)));
  if (id.startsWith("temp-")) return Promise.resolve(); // not persisted yet; local-only
  return api
    .put("/crm/deals", toApi(patch), { params: { id } })
    .then(() => undefined)
    .catch((err) => {
      // eslint-disable-next-line no-console
      console.error("[dealsService] updateDeal failed:", err);
      // Se revierte lo optimista y se dice por qué (p. ej. 422 «motivo obligatorio», 403).
      setDeals(prev);
      void Swal.fire({ icon: "error", title: "No se guardó el negocio", text: apiErrorMessage(err) });
    });
}

export function moveDeal(id: string, stage: DealStage): void {
  // Clear a stale manual probability so the new stage default applies, and drop
  // any lost reason when re-opening a deal.
  updateDeal(id, {
    stage,
    probability: null,
    lostReason: stage === "perdido" ? deals.find((d) => d.id === id)?.lostReason : undefined,
  });
}

export function removeDeal(id: string): void {
  const prev = deals;
  setDeals(deals.filter((d) => d.id !== id));
  if (id.startsWith("temp-")) return;
  api.delete("/crm/deals", { params: { id } }).catch((err) => {
    // eslint-disable-next-line no-console
    console.error("[dealsService] removeDeal failed:", err);
    setDeals(prev);
  });
}

/**
 * Refleja en el store el puntero de demo que la API acaba de devolver (#1318),
 * para que el distintivo de la tarjeta cambie sin recargar todos los negocios.
 * Sólo local: el puntero lo escribe el backend, nunca esta pantalla.
 */
export function setDealDemo(id: string, demo: DealDemoPointer | null): void {
  setDeals(deals.map((d) => (d.id === id ? { ...d, demo } : d)));
}

/** Re-fetch deals from the backend (the board's refresh action). */
export function resetDeals(): void {
  void loadDeals(true);
}

// --- Helpers ----------------------------------------------------------------

/** Latest createdAt across deals — a stable "today" for new-deal defaults. */
function mostRecentDate(): string {
  return deals.reduce((max, d) => (d.createdAt > max ? d.createdAt : max), "2026-01-01");
}

export function formatMoney(value: number, compact = false): string {
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    maximumFractionDigits: 0,
    notation: compact ? "compact" : "standard",
  }).format(value);
}

/** "2026-07" period key from an ISO date. */
export function monthKey(iso: string): string {
  return iso.slice(0, 7);
}

const MONTHS_ES = [
  "Ene", "Feb", "Mar", "Abr", "May", "Jun",
  "Jul", "Ago", "Sep", "Oct", "Nov", "Dic",
];

/** "Jul 2026" label from a "2026-07" key. */
export function monthLabel(key: string): string {
  const [y, m] = key.split("-");
  const idx = Number(m) - 1;
  return `${MONTHS_ES[idx] ?? m} ${y}`;
}

/** Quarter key like "2026-T3" from an ISO date. */
export function quarterKey(iso: string): string {
  const [y, m] = iso.split("-");
  const q = Math.floor((Number(m) - 1) / 3) + 1;
  return `${y}-T${q}`;
}
